/**
 * "Won — create customer" on a lead, with `v1_completion` on (v1 completion
 * C5, mock-up state 9): the "New customer" form filled from the lead, its
 * owner as the sales rep when they are one. Creating it moves the lead to
 * First order and links it to the new CIS customer, in one call.
 */
import { useEffect, useId, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type {
  LeadDetail,
  LeadListItem,
  NewCustomerForm,
} from "@shared-core/data/api-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { mutationErrorMessage } from "@shared-core/domain/mutation-error-message";
import {
  ORDER_CONTACT_NOT_SAVED_NOTE,
  bindNewCustomerErrors,
  fieldsNeedAFix,
  newCustomerDraftFromLead,
  toCreateCisCustomerRequest,
  wontSeeNewCustomer,
  type NewCustomerDraft,
  type NewCustomerField,
} from "@shared-core/domain/new-customer";
import {
  NewCustomerFields,
  NotInPortfolioNotice,
} from "../customers/NewCustomerFields";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";

export function LeadCreateCustomerDialog({
  lead,
  open,
  onOpenChange,
  onConverted,
}: {
  lead: Pick<LeadDetail, "id" | "company_name" | "address" | "owner_user_id"> &
    Partial<Pick<LeadDetail, "contacts">>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConverted: (lead: LeadListItem) => void;
}) {
  const adapter = useDataAdapter();
  const idPrefix = useId();
  const [form, setForm] = useState<NewCustomerForm | null>(null);
  const [draft, setDraft] = useState<NewCustomerDraft>(() =>
    newCustomerDraftFromLead(lead, null, []),
  );
  const [errors, setErrors] = useState<
    Partial<Record<NewCustomerField, string>>
  >({});
  const [failure, setFailure] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Filled afresh each time it opens, once the form's people and the
  // countries (to match the lead's free-text country) are in.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setErrors({});
    setFailure(null);
    Promise.all([
      adapter.getNewCustomerForm().catch(() => null),
      adapter.listAddressCountries().catch(() => []),
    ]).then(([loaded, countries]) => {
      if (cancelled) return;
      setForm(loaded);
      setDraft(newCustomerDraftFromLead(lead, loaded, countries));
    });
    return () => {
      cancelled = true;
    };
  }, [adapter, open, lead]);

  const create = async () => {
    setSaving(true);
    setErrors({});
    setFailure(null);
    try {
      const request = toCreateCisCustomerRequest(draft, {
        withMarket: false,
        withOrderContact: true,
      });
      const converted = await adapter.convertLeadToCisCustomer(lead.id, {
        name: request.name,
        account_address: request.account_address,
        owners: request.owners ?? {
          sales_rep_user_id: null,
          csr_user_id: null,
        },
        order_contact: request.order_contact,
      });
      if (converted.order_contact_not_saved)
        toast.warning(ORDER_CONTACT_NOT_SAVED_NOTE);
      onConverted(converted);
    } catch (err) {
      const fieldErrors = extractFieldErrors(err);
      if (fieldErrors.length > 0) {
        const bound = bindNewCustomerErrors(fieldErrors);
        setErrors(bound.byField);
        setFailure([fieldsNeedAFix(bound.count), ...bound.unbound].join(" "));
      } else {
        setFailure(
          mutationErrorMessage(err, "The customer wasn't created. Try again."),
        );
      }
    } finally {
      setSaving(false);
    }
  };

  const owner = form?.sales_rep_options?.find(
    (o) => o.user_id === lead.owner_user_id,
  );

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!saving) onOpenChange(next);
      }}
    >
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Create customer from this lead</DialogTitle>
        </DialogHeader>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          {failure && (
            <p
              role="alert"
              className="rounded-md border border-red-300 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:text-red-400"
            >
              {failure}
            </p>
          )}
          {form && wontSeeNewCustomer(form, draft) && <NotInPortfolioNotice />}
          <NewCustomerFields
            idPrefix={idPrefix}
            form={form}
            draft={draft}
            onDraftChange={setDraft}
            errors={errors}
            disabled={saving}
            showMarket={false}
            showOrderContact
            ownerNote={
              owner
                ? { userId: owner.user_id, label: "(lead owner)" }
                : undefined
            }
          />
          <p className="text-xs text-muted-foreground">
            The lead moves to First order and links to the new customer.
          </p>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving && <Loader2 className="size-3.5 animate-spin" />}
              Create customer
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
